import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { PeriodFilter } from "@/components/PeriodFilter";
import { DatePicker } from "@/components/DatePicker";
import { KpiCard } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { fmtDA, fmtDate, todayISO, MODES_LABEL } from "@/lib/format";
import { Plus, Search, Receipt, Loader2, Trash2, Paperclip } from "lucide-react";
import { FileUpload } from "@/components/FileUpload";
import { openFile } from "@/lib/files";
import { toast } from "sonner";

const MODES = ["especes", "banque", "virement", "cheque", "carte", "autre"];
const BANK_MODES = ["banque", "virement", "cheque", "carte"];

const emptyForm = () => ({
  societe_id: "",
  date_paiement: todayISO(),
  categorie_id: "",
  sous_categorie: "",
  description: "",
  beneficiaire: "",
  montant: "",
  mode_paiement: "especes",
  compte_id: "",
  reference_paiement: "",
  observations: "",
  piece: null,
});

export default function Depenses() {
  const { societes, societeParam, societeId } = useApp();
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/depenses", { params: { ...societeParam, period, search: search || undefined } });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [societeId, period, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.filter((c) => !c.parent_id)));
  }, []);

  const openNew = () => {
    const f = emptyForm();
    if (societeId !== "all") f.societe_id = societeId;
    else if (societes.length === 1) f.societe_id = societes[0].id;
    setForm(f);
    loadComptes(f.societe_id);
    setOpen(true);
  };

  const loadComptes = (sid) => {
    if (!sid) return setComptes([]);
    api.get("/banque/comptes", { params: { societe_id: sid } }).then((r) => setComptes(r.data));
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.societe_id) return toast.error("Sélectionnez une société.");
    if (!form.description) return toast.error("La description est requise.");
    if (!form.montant || Number(form.montant) <= 0) return toast.error("Montant invalide.");
    setSaving(true);
    try {
      const cat = categories.find((c) => c.id === form.categorie_id);
      await api.post("/depenses", {
        ...form,
        montant: Number(form.montant),
        categorie_name: cat?.name || "",
        compte_id: BANK_MODES.includes(form.mode_paiement) ? form.compte_id || null : null,
        piece_id: form.piece?.id || null,
        piece_nom: form.piece?.filename || null,
      });
      toast.success("Dépense enregistrée.");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cancelDep = async (id) => {
    try {
      await api.delete(`/depenses/${id}`);
      toast.success("Dépense annulée.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Dépenses"
        subtitle="Suivi des dépenses par société, catégorie et mode de paiement"
        testid="depenses-page"
        actions={
          can("depenses") ? (
          <Button onClick={openNew} className="gap-2" data-testid="new-depense-btn">
            <Plus className="h-4 w-4" /> Nouvelle dépense
          </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 kpi-grid">
        <KpiCard testid="dep-total" label="Total période" value={total} icon={Receipt} tone="primary" />
        <KpiCard testid="dep-count" label="Nombre d'opérations" value={items.length} isCurrency={false} icon={Receipt} />
        <KpiCard testid="dep-moyenne" label="Montant moyen" value={items.length ? total / items.length : 0} icon={Receipt} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
        <PeriodFilter value={period} onChange={setPeriod} />
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="dep-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="pl-9" />
        </div>
      </div>

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={Receipt} title="Aucune dépense" hint="Enregistrez votre première dépense pour la voir apparaître ici." />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="depenses-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Numéro</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Bénéficiaire</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id} data-testid={`dep-row-${d.id}`} className={d.statut === "annule" ? "opacity-50" : ""}>
                    <TableCell className="tabular text-xs font-medium">{d.numero}</TableCell>
                    <TableCell className="tabular text-sm whitespace-nowrap">{fmtDate(d.date_paiement)}</TableCell>
                    <TableCell className="text-sm">{d.categorie_name || "—"}</TableCell>
                    <TableCell className="text-sm max-w-[240px] truncate">{d.description}</TableCell>
                    <TableCell className="text-sm">{d.beneficiaire || "—"}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-normal">{MODES_LABEL[d.mode_paiement]}</Badge></TableCell>
                    <TableCell className="tabular text-right font-semibold whitespace-nowrap">{fmtDA(d.montant)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 justify-end">
                        {d.piece_id && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" title={d.piece_nom || "Justificatif"} onClick={() => openFile(d.piece_id)} data-testid={`dep-piece-${d.id}`}>
                            <Paperclip className="h-4 w-4" />
                          </Button>
                        )}
                        {can("depenses") && d.statut !== "annule" && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`dep-cancel-${d.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-popover">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Annuler cette dépense ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                La dépense {d.numero} sera annulée et le mouvement de caisse/banque contre-passé. L'historique est conservé.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Retour</AlertDialogCancel>
                              <AlertDialogAction onClick={() => cancelDep(d.id)} className="bg-destructive text-destructive-foreground">Annuler la dépense</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="depense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Nouvelle dépense</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Société *</Label>
              <Select value={form.societe_id} onValueChange={(v) => { setField("societe_id", v); loadComptes(v); }}>
                <SelectTrigger data-testid="dep-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date de paiement *</Label>
              <DatePicker value={form.date_paiement} onChange={(v) => setField("date_paiement", v)} testid="dep-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Catégorie</Label>
              <Select value={form.categorie_id} onValueChange={(v) => setField("categorie_id", v)}>
                <SelectTrigger data-testid="dep-categorie"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent className="bg-popover max-h-64">
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sous-catégorie</Label>
              <Input value={form.sous_categorie} onChange={(e) => setField("sous_categorie", e.target.value)} placeholder="Optionnel" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Description *</Label>
              <Input data-testid="dep-description" value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Objet de la dépense" />
            </div>
            <div className="space-y-1.5">
              <Label>Bénéficiaire / Payé à</Label>
              <Input value={form.beneficiaire} onChange={(e) => setField("beneficiaire", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Montant (DA) *</Label>
              <Input data-testid="dep-montant" type="number" step="0.01" value={form.montant} onChange={(e) => setField("montant", e.target.value)} placeholder="0,00" />
            </div>
            <div className="space-y-1.5">
              <Label>Mode de paiement *</Label>
              <Select value={form.mode_paiement} onValueChange={(v) => setField("mode_paiement", v)}>
                <SelectTrigger data-testid="dep-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {MODES.map((m) => <SelectItem key={m} value={m}>{MODES_LABEL[m]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {BANK_MODES.includes(form.mode_paiement) && (
              <div className="space-y-1.5">
                <Label>Compte bancaire</Label>
                <Select value={form.compte_id} onValueChange={(v) => setField("compte_id", v)}>
                  <SelectTrigger data-testid="dep-compte"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {comptes.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun compte pour cette société</div>}
                    {comptes.map((c) => <SelectItem key={c.id} value={c.id}>{c.banque} · {c.nom_compte}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Référence de paiement</Label>
              <Input value={form.reference_paiement} onChange={(e) => setField("reference_paiement", e.target.value)} placeholder="N° chèque, virement…" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Observations</Label>
              <Textarea value={form.observations} onChange={(e) => setField("observations", e.target.value)} rows={2} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Pièce justificative</Label>
              <FileUpload value={form.piece} onChange={(v) => setField("piece", v)} testid="dep-piece" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="dep-submit" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
